import { Text } from "@mantine/core";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { PartDto } from "#modules/parts/infra/Part.dto";
import { useUpdatePartDimensions } from "#modules/parts/view/useUpdatePartDimensions";
import { useUpdatePartWeight } from "#modules/parts/view/useUpdatePartWeight";
import { Group } from "#shared/view/components/Group";
import { Row } from "#shared/view/components/Thumbnails/AnalysisResult/AnalysisResultInformationSection";
import { Title } from "#shared/view/components/Title";
import { NumberInput } from "#shared/view/designSystem/inputs/NumberInput";
import {
  checkPartDimensions,
  getMaterialDimensionsFieldsConfiguration,
  InvalidFields,
  isDimensionInvalid,
  QuoteRequestLine,
} from "shared";

function WeightRow({
  part,
  quoteRequestLineId,
  isInvalid,
}: {
  part: PartDto;
  quoteRequestLineId: string;
  isInvalid: boolean;
}) {
  const { t } = useTranslation();
  const [weight, setWeight] = useState<number | undefined>(part.weight ?? undefined);
  const { updatePartWeight } = useUpdatePartWeight({ partId: part.id });

  const handleBlur = () => {
    if (!weight || weight <= 0) {
      return;
    }
    if (weight === part.weight) {
      return;
    }
    updatePartWeight({
      id: part.id,
      data: { weight, quoteRequestLineId },
    });
  };

  return (
    <Group justify="space-between" py={3} wrap="nowrap" gap="sm">
      <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
        {t("part.analysis2D.weight")}
      </Text>
      <NumberInput
        value={weight}
        onChange={setWeight}
        onBlur={handleBlur}
        suffix=" kg"
        min={0.01}
        size="xs"
        w="60%"
        error={isInvalid ? "Champ requis pour chiffrer" : undefined}
      />
    </Group>
  );
}

function DimensionsRows({
  part,
  quoteRequestLine,
  invalidFields,
}: {
  part: PartDto;
  quoteRequestLine: QuoteRequestLine;
  invalidFields: InvalidFields;
}) {
  const { t } = useTranslation();

  const fieldsConfig = useMemo(
    () => getMaterialDimensionsFieldsConfiguration(part.type ?? undefined),
    [part.type],
  );

  const initialValues = useMemo(
    () =>
      Object.fromEntries(
        fieldsConfig.map((f) => [
          f.field,
          (part[f.field as keyof PartDto] as number | null) ?? undefined,
        ]),
      ),
    [part, fieldsConfig],
  );

  const [values, setValues] = useState<Record<string, number | undefined>>(initialValues);

  const { updatePartDimensions } = useUpdatePartDimensions({
    partId: part.id,
    quoteRequestLine,
    layoutId: null,
  });

  const handleBlur = (field: string) => {
    updatePartDimensions({
      id: part.id,
      data: { [field]: values[field], quoteRequestLineId: quoteRequestLine.id },
    });
  };

  const partDimensionCheck = checkPartDimensions(part);

  return (
    <>
      {fieldsConfig.map((fieldConfig) => {
        const value = values[fieldConfig.field];
        const isInvalid =
          isDimensionInvalid(fieldConfig.field, value, invalidFields) || !value || value <= 0;

        return (
          <Group key={fieldConfig.key} justify="space-between" py={3} wrap="nowrap" gap="sm">
            <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
              {t(`material.${fieldConfig.key}`)}
            </Text>
            <NumberInput
              value={value}
              onChange={(v) => {
                setValues((prev) => {
                  const next = { ...prev, [fieldConfig.field]: v };
                  if (fieldConfig.syncWith) {
                    next[fieldConfig.syncWith] = v;
                  }
                  return next;
                });
              }}
              onBlur={() => handleBlur(fieldConfig.field)}
              unit="mm"
              size="xs"
              w="60%"
              error={
                isInvalid
                  ? "Champ requis pour chiffrer"
                  : partDimensionCheck.hasError &&
                      partDimensionCheck.error === "volume_out_of_bounds"
                    ? "Dimensions incohérentes"
                    : undefined
              }
            />
          </Group>
        );
      })}
    </>
  );
}

export const AnalysisResultDimensionsSection = ({
  part,
  quoteRequestLine,
  invalidFields,
}: {
  part: PartDto;
  quoteRequestLine: QuoteRequestLine | null;
  invalidFields: InvalidFields;
}) => {
  const { t } = useTranslation();
  const isWeightInvalid = invalidFields.has("weight");

  return (
    <>
      <Title order={5} my={6}>
        {t("part.analysis2D.sections.dimensions")}
      </Title>
      {quoteRequestLine ? (
        <WeightRow
          part={part}
          quoteRequestLineId={quoteRequestLine.id}
          isInvalid={isWeightInvalid}
        />
      ) : (
        <Row label={t("part.analysis2D.weight")} value={part.weight ? `${part.weight} kg` : null} />
      )}
      {quoteRequestLine ? (
        <DimensionsRows
          key={part.type}
          part={part}
          quoteRequestLine={quoteRequestLine}
          invalidFields={invalidFields}
        />
      ) : (
        <>
          <Row label={t("material.length")} value={part.length ? `${part.length} mm` : null} />
          <Row label={t("material.width")} value={part.width ? `${part.width} mm` : null} />
          <Row label={t("material.height")} value={part.height ? `${part.height} mm` : null} />
        </>
      )}
      <Row label={t("part.analysis2D.volume")} value={part.volume ? `${part.volume} mm³` : null} />
    </>
  );
};
